import { ContactlessOptionsEntity } from '@app/modules/admin/services/contactless-options.service'

export interface OptionsSettingsGroup {
  id: string
  title: string
  keys: string[]
  note?: string
}

/** Form/persona options — section order matches the Admin Tools catalog payload. */
export const FORM_OPTIONS_GROUPS: OptionsSettingsGroup[] = [
  {
    id: 'theme',
    title: 'Theme',
    keys: ['theme'],
    note: 'Applies only to this persona.'
  },
  { id: 'translate', title: 'Translations', keys: ['translate'] },
  {
    id: 'access',
    title: 'Registration lock',
    keys: ['locked'],
    note: 'Turn this on to stop people from editing their registration information in this persona.'
  },
  {
    id: 'confirmation',
    title: 'Confirmation',
    keys: ['hideQrCode', 'hideDateTime', 'hideLocation', 'hideEditButton', 'editLabel', 'hideWalletButton'],
    note: 'Applies to the confirmation email and the on-screen confirmation.'
  },
  {
    id: 'invites',
    title: 'Invites & import',
    keys: ['allowInvitation', 'allowImport'],
    note: 'Allow invitation shows or hides the Invite button. Allow import shows or hides the Import button.'
  },
  {
    id: 'messages',
    title: 'Messages',
    keys: ['pendingPaymentMessage']
  },
  { id: 'email', title: 'Email', keys: ['notifyParent'] }
]

/**
 * Full persona options skeleton from the catalog payload.
 * Used to show every known field even when the API object is sparse.
 */
export const FORM_OPTIONS_DEFAULTS: Record<string, unknown> = {
  theme: {
    logo: null,
    enable: false,
    palette: {
      background: '#2E3138',
      button: '#1F60DF',
      cardBackground: '#5B6271'
    }
  },
  translate: {
    ara: {
      name: ''
    }
  },
  locked: false,
  hideQrCode: false,
  hideDateTime: false,
  hideLocation: false,
  hideEditButton: false,
  editLabel: 'Edit your info',
  hideWalletButton: false,
  allowInvitation: true,
  allowImport: false,
  pendingPaymentMessage: '',
  notifyParent: false
}

export const EVENT_OPTIONS_GROUPS: OptionsSettingsGroup[] = [
  { id: 'translate', title: 'Translations', keys: ['translate'] },
  {
    id: 'branding',
    title: 'Branding & invitation',
    keys: ['isWhiteLabeled', 'invitationSignoff']
  }
]

/** Event options skeleton from the Admin Tools catalog — Add chips only, never auto-seeded. */
export const EVENT_OPTIONS_DEFAULTS: Record<string, unknown> = {
  translate: {
    ara: {
      name: ''
    }
  },
  isWhiteLabeled: false,
  invitationSignoff: ''
}

export function optionsGroupsForEntity(entity: ContactlessOptionsEntity): OptionsSettingsGroup[] {
  switch (entity) {
    case 'form':
      return FORM_OPTIONS_GROUPS
    case 'event':
      return EVENT_OPTIONS_GROUPS
    default:
      return []
  }
}

const HIDDEN_EVENT_KEYS = ['theme', 'dct', 'dtcm', 'front_fixed_element', 'seatsIO', 'brella', 'reminders', 'other', 'hideInvitationMessage'] as const

const HIDDEN_FORM_KEYS = [
  'dct',
  'dtcm',
  'snoball',
  'integrations',
  'handshakes',
  'brella',
  'b2b2go',
  'payment',
  'reminders',
  'ai_approve',
  'front_fixed_element',
  'background_preprinted',
  'font_family_name',
  'barcodeColor',
  'minimumEntries',
  'maximumEntries',
  'titles',
  'autoApproveInvitation',
  'facialRecognition',
  'emailTemplate',
  'pendingApprovalMessage'
] as const

export function hiddenOptionKeysForEntity(entity: ContactlessOptionsEntity): readonly string[] {
  if (entity === 'event') {
    return HIDDEN_EVENT_KEYS
  }
  if (entity === 'form') {
    return HIDDEN_FORM_KEYS
  }
  return []
}

export function optionsDefaultsForEntity(entity: ContactlessOptionsEntity): Record<string, unknown> | null {
  switch (entity) {
    case 'form':
      return FORM_OPTIONS_DEFAULTS
    case 'event':
      return EVENT_OPTIONS_DEFAULTS
    default:
      return null
  }
}
